/**
 * localStorage with Time-To-Live (TTL) Support
 *
 * Wrapper around localStorage that adds automatic expiration to stored data.
 * Prevents localStorage bloat and ensures compliance with data retention policies.
 *
 * All data is stored in the format: `{ value: T, expiresAt: number }`
 *
 * @module localStorageWithTTL
 *
 * @example
 * ```typescript
 * // Store data with 7-day expiration
 * setItemWithTTL('user_session', { userId: '123' }, TTL.ONE_WEEK);
 *
 * // Retrieve data (returns null if expired)
 * const session = getItemWithTTL('user_session');
 *
 * // Clean up expired items on app startup
 * clearExpiredItems();
 * ```
 */

/**
 * Internal storage format wrapping the actual value with expiration timestamp
 * @internal
 */
interface StorageItemWithTTL<T> {
  value: T;
  expiresAt: number; // Unix timestamp in milliseconds
}

/**
 * Set an item in localStorage with TTL
 * @param key Storage key
 * @param value Value to store (will be JSON stringified)
 * @param ttlMs Time-to-live in milliseconds (default: 30 days)
 */
export const setItemWithTTL = <T>(
  key: string,
  value: T,
  ttlMs: number = 30 * 24 * 60 * 60 * 1000 // 30 days default
): void => {
  try {
    const item: StorageItemWithTTL<T> = {
      value,
      expiresAt: Date.now() + ttlMs,
    };
    localStorage.setItem(key, JSON.stringify(item));
  } catch (error) {
    console.error(`Error setting localStorage item with TTL: ${key}`, error);
  }
};

/**
 * Get an item from localStorage, checking TTL
 * Returns null if item doesn't exist or has expired
 * @param key Storage key
 */
export const getItemWithTTL = <T>(key: string): T | null => {
  try {
    const itemStr = localStorage.getItem(key);
    if (!itemStr) {
      return null;
    }

    const item: StorageItemWithTTL<T> = JSON.parse(itemStr);

    // Check if expired
    if (Date.now() > item.expiresAt) {
      localStorage.removeItem(key);
      return null;
    }

    return item.value;
  } catch (error) {
    console.error(`Error getting localStorage item with TTL: ${key}`, error);
    return null;
  }
};

/**
 * Remove an item from localStorage
 * @param key Storage key
 */
export const removeItemWithTTL = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Error removing localStorage item: ${key}`, error);
  }
};

/**
 * Clear all expired items from localStorage
 * Useful to run on app startup to clean up stale data
 *
 * Performance optimization: Only runs once per session to prevent UI jank
 * on slow devices. Uses sessionStorage to track if cleanup already ran.
 */
export const clearExpiredItems = (): void => {
  try {
    // Check if we've already cleaned up this session
    const sessionKey = '__ttl_cleanup_done';
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(sessionKey)) {
      return; // Already cleaned up this session, skip
    }

    const keysToRemove: string[] = [];
    const now = Date.now();

    // Iterate through all keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;

      try {
        const itemStr = localStorage.getItem(key);
        if (!itemStr) continue;

        const item = JSON.parse(itemStr);

        // Check if this is a TTL item and if it's expired
        if (item.expiresAt && typeof item.expiresAt === 'number' && now > item.expiresAt) {
          keysToRemove.push(key);
        }
      } catch {
        // Skip items that aren't JSON or don't have the expected structure
        continue;
      }
    }

    // Remove expired items
    keysToRemove.forEach(key => localStorage.removeItem(key));

    if (keysToRemove.length > 0) {
      console.log(`Cleared ${keysToRemove.length} expired localStorage items`);
    }

    // Mark cleanup as done for this session
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(sessionKey, 'true');
    }
  } catch (error) {
    console.error('Error clearing expired items from localStorage', error);
  }
};

/**
 * Common TTL durations (in milliseconds)
 */
export const TTL = {
  ONE_HOUR: 60 * 60 * 1000,
  ONE_DAY: 24 * 60 * 60 * 1000,
  ONE_WEEK: 7 * 24 * 60 * 60 * 1000,
  ONE_MONTH: 30 * 24 * 60 * 60 * 1000,
  THREE_MONTHS: 90 * 24 * 60 * 60 * 1000,
  ONE_YEAR: 365 * 24 * 60 * 60 * 1000,
} as const;
